import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { MessageSquare, Clock, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupportDashboard() {
  const { agent } = useOutletContext();
  const [data, setData] = useState(null);
  const [activeChats, setActiveChats] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/support/dashboard`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActiveChats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chat/active`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setActiveChats(d.chats || []);
      }
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDashboard();
    fetchActiveChats();
    const interval = setInterval(fetchActiveChats, 10000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchActiveChats]);

  if (!data) return <div className="text-white/50">Loading dashboard...</div>;

  const stats = [
    { label: 'Open Tickets', value: data.queue.open, icon: MessageSquare, color: 'text-blue-400' },
    { label: 'In Progress', value: data.queue.in_progress, icon: Clock, color: 'text-amber-400' },
    { label: 'Awaiting User', value: data.queue.awaiting_user, icon: AlertCircle, color: 'text-orange-400' },
    { label: 'Resolved Today', value: data.queue.resolved_today, icon: CheckCircle, color: 'text-green-400' },
  ];

  return (
    <div data-testid="support-dashboard" className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Support Dashboard</h1>
        <p className="text-white/50 mt-1">Welcome back, {agent?.name || 'Agent'}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </div>
            <div className="text-2xl font-bold text-white">{s.value}</div>
            <div className="text-white/40 text-sm">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-2">My Open Tickets</h3>
          <div className="text-3xl font-bold text-white">{data.my_stats.open}</div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-2">My Resolved Today</h3>
          <div className="text-3xl font-bold text-green-400">{data.my_stats.resolved_today}</div>
        </div>
      </div>

      {/* Live Chats */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="live-chats-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Active Live Chats</h2>
          </div>
          {activeChats.length > 0 && (
            <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">{activeChats.length} active</span>
          )}
        </div>
        {activeChats.length === 0 ? (
          <p className="text-white/30 text-sm">No active chats right now</p>
        ) : (
          <div className="space-y-2">
            {activeChats.map(chat => (
              <Link
                key={chat.id}
                to={`/support/chat/${chat.id}`}
                className="flex items-center justify-between bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors"
                data-testid={`chat-${chat.id}`}
              >
                <div>
                  <p className="text-white text-sm font-medium">{chat.user_name || chat.user_email}</p>
                  <p className="text-white/30 text-xs">Started {new Date(chat.created_at).toLocaleTimeString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">Active</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
